import {
  Badge,
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Image,
  Input,
  Link,
  Progress,
  Radio,
  RadioGroup,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { MdAdd, MdContentCopy, MdDelete, MdHelpOutline, MdUploadFile } from 'react-icons/md';
import { useForm, useFieldArray, Controller, Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ActionMap,
  BotTag,
  TaskAction,
  TaskFormData,
  TaskType,
  actions,
} from '@/lib/sdk/chatbot/types';
import { FormField } from '@/components/Modal/FormField';
import { StartnewConfigSection } from './StartnewConfigSection';
import { SkippableConfigSection } from './SkippableConfigSection';
import { MakerCheckerConfigSection } from './MakerCheckerConfigSection';
import { ExtractionConfigSection } from './ExtractionConfigSection';
import { uploadFileToS3 } from '@/lib/s3/upload';
import { GridRow } from './useTaskGridReducer';
import { ValidationConfigSection } from './ValidationConfigSection';
import { DataSourceConfigSection } from './DataSourceConfigSection';
import { AutoFillConfigSection } from './AutoFillConfigSection';
import { WritebackConfigSection, type WritebackPolicyDraft } from './WritebackConfigSection';
import { buildValueRefTasks, type PanelTask } from './valueRefTasks';
import { parseGoogleMapsUrl, parseCoordString, buildCoordString } from './locationUtils';
import { documentLinkUrlSchema, taskFormSchema } from '@/lib/sdk/chatbot/taskFormSchema';
import { useFieldDefinitions, useFieldDefinitionsForDatasets } from '@/lib/sdk/master-data-field-definitions/queries';
import { useMasterDataDatasets } from '@/lib/sdk/master-data/queries';

// ─── Schema ────────────────────────────────────────────────────────────────────
// Shared with the V3 TaskModal via taskFormSchema so the two workflow-builder
// generations validate identically (name/type required, auto-skip needs a
// condition task) instead of the panel silently accepting what the modal rejects.

const panelSchema = taskFormSchema;

// Task types that may fire a one-way team alert (TEAM_ALERT action). Today only
// the intro task triggers it (guest check-in), but the mechanism is generic —
// add a type here once its Process/Check fires platform.TeamAlertMessage.
// issue #711: NOTIFY (TEAM_ALERT) can fire from the intro task (guest check-in) or the
// end task (process completion, e.g. handing a route card off to the next station).
const TEAM_ALERT_ALLOWED_TASK_TYPES = [TaskType.INTRO, TaskType.END];

// G11: master-data writeback (WRITEBACK action) fires from the same completion point
// as end-task TEAM_ALERT — only on_list_complete, matching internal/workflow/types/end.go.
const WRITEBACK_ALLOWED_TASK_TYPES = [TaskType.END];

// Reduces the Fields-to-Patch row editor's entries to the field->valueRef map the
// backend expects, dropping any row still missing a field or value (e.g. a freshly
// added blank row). Shared between the live form-sync (so panelSchema's "at least one
// field" check sees real data, not the stale value form.reset last set) and handleSave,
// so the two can't drift on what counts as "configured".
function buildWritebackFields(entries: { field: string; value: string }[]): Record<string, string> {
  return entries.reduce((acc, { field, value }) => {
    if (field.trim() && value.trim()) acc[field.trim()] = value.trim();
    return acc;
  }, {} as Record<string, string>);
}

// Splits the comma-separated Shared Identities input into trimmed values, or
// undefined when none are left.
function splitSharedIdentities(text?: string): string[] | undefined {
  const values = (text ?? '').split(',').map((value) => value.trim()).filter(Boolean);
  return values.length > 0 ? values : undefined;
}

// Reduces the full policy-draft list (one per independent writeback target) to the
// shape the backend expects. Shared between the live form-sync and handleSave for
// the same reason buildWritebackFields is — one place decides what "configured"
// means so the two can't drift.
function buildWritebackConfigs(policies: WritebackPolicyDraft[]) {
  return policies.map((policy) => ({
    dataset: policy.dataset,
    keyRef: policy.keyRef.trim(),
    matchField: policy.matchField,
    mode: policy.mode,
    labelRef: policy.labelRef.trim(),
    parentKeyRef: policy.parentKeyRef.trim(),
    fields: buildWritebackFields(policy.fieldEntries),
  }));
}

// ─── Props ────────────────────────────────────────────────────────────────────


export interface TaskConfigPanelProps {
  isOpen: boolean;
  onClose: () => void;
  row: GridRow | null;
  rowIdx: number;
  onSave: (localId: string, config: Partial<GridRow>) => void;
  existingTasks: PanelTask[];
  /** Tasks after this one, offered as auto-fill targets. Defaults to none (e.g. subtask editor). */
  laterTasks?: PanelTask[];
  /** Tasks offered as the approval template's value source. Unlike existingTasks this
   * includes the task being configured, whose own submitted value can feed the template.
   * Defaults to existingTasks (previous tasks only). */
  approvalSourceTasks?: PanelTask[];
  chatbotLifecycle: string;
  availableWorkflows?: { short_code: string; title: string }[];
  /** Threaded through to DataSourceConfigSection — a Flows-enabled chatbot's
   * dynamic_choice fields allow more results (up to 200, rendered as a single
   * scrollable Dropdown) than the native channel's paginated list messages (up to 50,
   * sent in pages of 10). */
  flowsEnabled?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function TaskConfigPanel({
  isOpen,
  onClose,
  row,
  rowIdx,
  onSave,
  existingTasks,
  laterTasks = [],
  approvalSourceTasks,
  chatbotLifecycle,
  availableWorkflows = [],
  flowsEnabled,
}: TaskConfigPanelProps) {
  const toast = useToast();
  const [attributeMappingEntries, setAttributeMappingEntries] = useState<{ key: string; value: string }[]>([]);
  const [autoFillEntries, setAutoFillEntries] = useState<{ targetRow: string; value: string }[]>([]);
  const [writebackPolicies, setWritebackPolicies] = useState<WritebackPolicyDraft[]>([]);
  const [tags, setTags] = useState<BotTag[]>([]);
  const [urlInput, setUrlInput] = useState('');
  const [urlError, setUrlError] = useState('');
  const urlInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [mapsLinkInput, setMapsLinkInput] = useState('');
  const [locationState, setLocationState] = useState({ lat: '', lng: '', radius: '0' });
  const [imageUrl, setImageUrl] = useState('');
  const [isImageUploading, setIsImageUploading] = useState(false);
  const imageFileRef = useRef<HTMLInputElement>(null);
  const { orgId } = useParams<{ orgId: string }>();
  const form = useForm<TaskFormData>({
    resolver: zodResolver(panelSchema) as unknown as Resolver<TaskFormData>,
    defaultValues: {
      name: '',
      description: '',
      type: TaskType.REGULAR,
      data: '',
      actions: [],
      approvalConfig: { contactType: 'list', approvalContacts: [], approvalTemplate: '', approvalResolver: 'default', parserEnabled: false, parserType: '', makerPendingText: '', makerApprovedText: '', makerRejectedText: '', approverApprovedText: '', approverRejectedText: '' },
      extractionConfig: {
        enabled: false,
        parser_type: undefined,
        gov_id_type: undefined,
        gate: undefined,
        required_fields: undefined,
        optional_fields: undefined,
        min_confidence: undefined,
        store_result: undefined,
        failure_message: undefined,
        max_attempts: undefined,
      },
      startnewConfig: { targetMappingSource: 'links' },
      skipConfig: { conditions: [], conditionLogic: 'and', skipMode: 'manual' },
      writebackConfigs: [],
      mutable: false,
      maskOutput: false,
      requirePrefill: false,
      documentLinks: [],
    },
  });

  const { fields: docLinkFields, append: appendDocLink, remove: removeDocLink, replace: replaceDocLinks } = useFieldArray({
    control: form.control,
    name: 'documentLinks',
  });

  const watchedActions = form.watch('actions') ?? [];
  const watchedType = form.watch('type') ?? '';
  const watchedMasterDataDataset = form.watch('dataSourceConfig.dataset') ?? '';
  const watchedExtractionEnabled = form.watch('extractionConfig.enabled') ?? false;
  const watchedRequiredFields = form.watch('extractionConfig.required_fields') ?? [];
  const { data: masterDataFieldDefs = [] } = useFieldDefinitions(orgId ?? '', watchedMasterDataDataset);
  const writebackFieldDefsByDataset = useFieldDefinitionsForDatasets(orgId ?? '', writebackPolicies.map((p) => p.dataset));
  // The value picker lists every task with its row number as saved: earlier tasks, this
  // one (from the live form, so a dataset or extraction change shows at once) and later
  // ones, which it shows as unavailable.
  const watchedName = form.watch('name') ?? '';
  const watchedOptionalFields = form.watch('extractionConfig.optional_fields') ?? [];
  const currentTaskRow = existingTasks.length + 1;
  const pickerTasks: PanelTask[] = [
    ...existingTasks,
    {
      id: row?.id ?? '',
      name: watchedName.trim() || 'This task',
      index: currentTaskRow,
      type: watchedType,
      dataset: watchedMasterDataDataset || undefined,
      extractionFields: watchedExtractionEnabled ? [...watchedRequiredFields, ...watchedOptionalFields] : undefined,
    },
    ...laterTasks,
  ];
  const pickerFieldDefsByDataset = useFieldDefinitionsForDatasets(
    orgId ?? '',
    pickerTasks.map((task) => task.dataset ?? ''),
  );
  const valueRefTasks = buildValueRefTasks(pickerTasks, pickerFieldDefsByDataset, currentTaskRow);
  const { data: masterDataDatasets = [] } = useMasterDataDatasets(orgId ?? '');
  // text_required and location_check only get master_data attributes to auto-fill from
  // once a dataset is configured (source is set alongside dataset — see the Dataset
  // Select's onChange below); dynamic_choice always supports it regardless of its own
  // data source.
  const isMasterDataText = watchedType === TaskType.TEXT && !!watchedMasterDataDataset;
  const isMasterDataLocationCheck = watchedType === TaskType.LOCATION_CHECK && !!watchedMasterDataDataset;
  // photo_required/doc_required/media_required tasks with verification switched on mirror
  // their extracted fields onto this same task's own row (see extractionGate.mirrorFields,
  // internal/workflow/types/extraction.go), so {task_<this row>_attr:field} can pre-fill
  // downstream tasks exactly like a dynamic_choice/master_data selection does.
  const isExtractionSource =
    [TaskType.PHOTO, TaskType.DOCUMENT, TaskType.MEDIA].includes(watchedType as TaskType) && watchedExtractionEnabled;
  const showAutoFillSection =
    watchedType === TaskType.DYNAMIC_CHOICE || isMasterDataText || isMasterDataLocationCheck || isExtractionSource;
  const hasStartnew = watchedActions.includes(TaskAction.STARTNEW);
  const hasSkippable = watchedActions.includes(TaskAction.SKIPPABLE);
  const hasMakerChecker = watchedActions.includes(TaskAction.MAKERCHECKER);
  const showApprovalConfig = hasMakerChecker;
  const teamAlertAllowed = TEAM_ALERT_ALLOWED_TASK_TYPES.includes(watchedType as TaskType);
  const showTeamAlertConfig = teamAlertAllowed && watchedActions.includes(TaskAction.TEAM_ALERT);
  const writebackAllowed = WRITEBACK_ALLOWED_TASK_TYPES.includes(watchedType as TaskType);
  const showWritebackConfig = writebackAllowed && watchedActions.includes(TaskAction.WRITEBACK);

  // Centralized so every policy mutation (add/remove/edit) updates both the local
  // draft state the section renders from and the RHF-bound value panelSchema
  // validates against — the same live-sync fix that made the single-policy "at
  // least one field" check actually see what's configured.
  const syncWritebackPolicies = (next: WritebackPolicyDraft[]) => {
    setWritebackPolicies(next);
    form.setValue('writebackConfigs', buildWritebackConfigs(next), { shouldValidate: true });
  };
  const handleAddWritebackPolicy = () => {
    syncWritebackPolicies([...writebackPolicies, { dataset: '', matchField: undefined, keyRef: '', labelRef: '', parentKeyRef: '', fieldEntries: [] }]);
  };
  const handleRemoveWritebackPolicy = (index: number) => {
    syncWritebackPolicies(writebackPolicies.filter((_, i) => i !== index));
  };
  const handleWritebackPolicyChange = (index: number, patch: Partial<WritebackPolicyDraft>) => {
    syncWritebackPolicies(writebackPolicies.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  };

  // Seed one empty policy the moment WRITEBACK is checked, so the section appears
  // ready to fill in immediately — same as TEAM_ALERT/MakerChecker's config
  // sections, rather than showing an empty list the author has to click "Add" on
  // first. Only fires on the 0->nonzero transition; removing every policy while
  // WRITEBACK stays checked is a deliberate "not configured yet" state, not
  // something to re-seed out from under the author.
  useEffect(() => {
    if (showWritebackConfig && writebackPolicies.length === 0) {
      handleAddWritebackPolicy();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWritebackConfig]);

  // Reset form whenever the row changes (panel opens for a new row)
  useEffect(() => {
    if (!isOpen || !row) return;

    const mappingEntries = row.startnewConfig?.attributeMapping
      ? Object.entries(row.startnewConfig.attributeMapping).map(([key, value]) => ({ key, value }))
      : [];
    setAttributeMappingEntries(mappingEntries);

    const autoFillEntriesFromRow = row.autoFillConfig
      ? Object.entries(row.autoFillConfig).map(([targetRow, value]) => ({ targetRow, value }))
      : [];
    setAutoFillEntries(autoFillEntriesFromRow);

    const writebackPoliciesFromRow: WritebackPolicyDraft[] = (row.writebackConfigs ?? []).map((config) => ({
      dataset: config.dataset ?? '',
      matchField: config.matchField,
      mode: config.mode,
      keyRef: config.keyRef ?? '',
      labelRef: config.labelRef ?? '',
      parentKeyRef: config.parentKeyRef ?? '',
      fieldEntries: config.fields ? Object.entries(config.fields).map(([field, value]) => ({ field, value })) : [],
    }));
    setWritebackPolicies(writebackPoliciesFromRow);

    setTags(row.tags ?? []);
    setUrlInput('');
    setUrlError('');
    setMapsLinkInput('');
    if (row.data && (row.type === TaskType.LOCATION || row.type === TaskType.LOCATION_CHECK)) {
      setLocationState(parseCoordString(row.data));
    } else {
      setLocationState({ lat: '', lng: '', radius: '0' });
    }
    setImageUrl(row.type === TaskType.INTRO ? (row.data ?? '') : '');

    const docLinks = (row.documentLinks ?? []).map((url) => ({ url }));

    form.reset({
      name: row.name,
      description: row.description,
      type: row.type,
      data: row.data,
      actions: row.actions ?? [],
      approvalConfig: {
        // levels must round-trip verbatim — it's the only place multi-level state lives once
        // an author has added a second level, and dropping it here silently erases the whole
        // chain the next time this row is saved, even if nothing else was touched.
        levels: row.approvalConfig?.levels,
        completionPolicy: row.approvalConfig?.completionPolicy,
        maxBounces: row.approvalConfig?.maxBounces,
        contactType: row.approvalConfig?.contactType ?? 'list',
        approvalContacts: row.approvalConfig?.approvalContacts ?? [],
        approvalTemplate: row.approvalConfig?.approvalTemplate ?? '',
        approvalResolver: row.approvalConfig?.approvalResolver ?? 'default',
        templateBuilder: row.approvalConfig?.templateBuilder,
        parserEnabled: row.approvalConfig?.parserEnabled ?? false,
        parserType: row.approvalConfig?.parserType ?? '',
        valueSourceTaskId: row.approvalConfig?.valueSourceTaskId,
        makerPendingText: row.approvalConfig?.makerPendingText ?? '',
        makerApprovedText: row.approvalConfig?.makerApprovedText ?? '',
        makerRejectedText: row.approvalConfig?.makerRejectedText ?? '',
        approverApprovedText: row.approvalConfig?.approverApprovedText ?? '',
        approverRejectedText: row.approvalConfig?.approverRejectedText ?? '',
      },
      validationConfig: row.validationConfig ?? { type: undefined, threshold: undefined, reference_task: undefined },
      extractionConfig: {
        enabled: row.extractionConfig?.enabled ?? false,
        parser_type: row.extractionConfig?.parser_type ?? undefined,
        gov_id_type: row.extractionConfig?.gov_id_type ?? undefined,
        gate: row.extractionConfig?.gate ?? undefined,
        required_fields: row.extractionConfig?.required_fields ?? undefined,
        optional_fields: row.extractionConfig?.optional_fields ?? undefined,
        min_confidence: row.extractionConfig?.min_confidence ?? undefined,
        store_result: row.extractionConfig?.store_result ?? undefined,
        failure_message: row.extractionConfig?.failure_message ?? undefined,
        max_attempts: row.extractionConfig?.max_attempts ?? undefined,
      },
      dataSourceConfig: row.dataSourceConfig ?? {},
      startnewConfig: {
        targetChatbot: row.startnewConfig?.targetChatbot,
        targetMappingSource: 'links',
        targetTask: row.startnewConfig?.targetTask,
        defaultTargetKey: row.startnewConfig?.defaultTargetKey,
        attributeMapping: row.startnewConfig?.attributeMapping,
      },
      skipConfig: {
        conditions: row.skipConfig?.conditions ?? [],
        conditionLogic: row.skipConfig?.conditionLogic ?? 'and',
        skipMode: row.skipConfig?.skipMode ?? 'manual',
      },
      counterConfig: {
        identityTaskId: row.counterConfig?.identityTaskId,
        countField: row.counterConfig?.countField,
        codeTaskId: row.counterConfig?.codeTaskId,
        sharedIdentities: row.counterConfig?.sharedIdentities?.join(', '),
      },
      writebackConfigs: buildWritebackConfigs(writebackPoliciesFromRow),
      mutable: row.mutable,
      maskOutput: row.maskOutput,
      requirePrefill: row.requirePrefill,
      softTtlValue: row.softTtlValue,
      softTtlUnit: row.softTtlUnit,
      documentLinks: docLinks,
    });
    replaceDocLinks(docLinks);
  }, [isOpen, row?._localId]);

  const handleSave = form.handleSubmit((formData) => {
    const attributeMapping = attributeMappingEntries.length > 0
      ? attributeMappingEntries.reduce((acc, { key, value }) => {
          if (key.trim()) acc[key.trim()] = value;
          return acc;
        }, {} as Record<string, string>)
      : undefined;

    const isMasterDataTextSave = formData.type === TaskType.TEXT && !!formData.dataSourceConfig?.dataset;
    const isMasterDataLocationCheckSave = formData.type === TaskType.LOCATION_CHECK && !!formData.dataSourceConfig?.dataset;
    const isExtractionSourceSave =
      [TaskType.PHOTO, TaskType.DOCUMENT, TaskType.MEDIA].includes(formData.type as TaskType) &&
      !!formData.extractionConfig?.enabled;
    const autoFillConfig = (formData.type === TaskType.DYNAMIC_CHOICE || isMasterDataTextSave || isMasterDataLocationCheckSave || isExtractionSourceSave) && autoFillEntries.length > 0
      ? autoFillEntries.reduce((acc, { targetRow, value }) => {
          if (targetRow.trim() && value.trim()) acc[targetRow.trim()] = value.trim();
          return acc;
        }, {} as Record<string, string>)
      : undefined;

    const writebackConfigs = formData.actions?.includes(TaskAction.WRITEBACK)
      ? buildWritebackConfigs(writebackPolicies)
      : undefined;

    const startnewConfig = formData.actions?.includes(TaskAction.STARTNEW)
      ? {
          targetChatbot: formData.startnewConfig?.targetChatbot,
          targetMappingSource: 'links' as const,
          targetTask: formData.startnewConfig?.targetTask,
          defaultTargetKey: formData.startnewConfig?.defaultTargetKey,
          attributeMapping,
        }
      : undefined;

    const skipConfig = formData.actions?.includes(TaskAction.SKIPPABLE) &&
    formData.skipConfig?.conditions?.some(condition => condition.conditionTask !== undefined)
      ? {
          conditions: formData.skipConfig.conditions.filter(condition => condition.conditionTask !== undefined),
          conditionLogic: formData.skipConfig.conditionLogic ?? 'and',
          skipMode: formData.skipConfig.skipMode ?? 'manual',
        }
      : undefined;

    const isLocationTask =
      formData.type === TaskType.LOCATION || formData.type === TaskType.LOCATION_CHECK;
    const isIntroTask = formData.type === TaskType.INTRO;
    const isAddressTask = formData.type === TaskType.ADDRESS;

    const config: Partial<GridRow> = {
      actions: (formData.actions ?? []) as TaskAction[],
      approvalConfig: formData.approvalConfig,
      validationConfig: formData.validationConfig,
      extractionConfig: formData.extractionConfig?.enabled ? formData.extractionConfig : undefined,
      dataSourceConfig: formData.dataSourceConfig,
      startnewConfig,
      autoFillConfig,
      skipConfig,
      counterConfig: formData.type === TaskType.COUNTER
        ? {
            identityTaskId: formData.counterConfig?.identityTaskId,
            countField: formData.counterConfig?.countField,
            codeTaskId: formData.counterConfig?.codeTaskId,
            sharedIdentities: formData.counterConfig?.identityTaskId
              ? splitSharedIdentities(formData.counterConfig?.sharedIdentities)
              : undefined,
          }
        : undefined,
      writebackConfigs,
      mutable: formData.mutable ?? false,
      maskOutput: formData.maskOutput ?? false,
      requirePrefill: formData.requirePrefill ?? false,
      softTtlValue: formData.softTtlValue,
      softTtlUnit: formData.softTtlUnit,
      documentLinks: (formData.documentLinks ?? []).map((item) => item.url),
      tags: tags.filter((t) => t.key.trim() !== ''),
      ...(isLocationTask && locationState.lat && locationState.lng
        ? { data: buildCoordString(locationState.lat, locationState.lng, locationState.radius) }
        : {}),
      ...(isIntroTask && imageUrl ? { data: imageUrl } : {}),
      ...(isAddressTask ? { data: formData.data ?? '' } : {}),
    };

    if (row) {
      onSave(row._localId, config);
    }
    onClose();
  });

  const handleImageUpload = useCallback(async (file: File) => {
    try {
      setIsImageUploading(true);
      const { url } = await uploadFileToS3(file, { folder: 'task-intro-images' });
      setImageUrl(url);
    } catch {
      toast({ title: 'Image upload failed', status: 'error', duration: 3000 });
    } finally {
      setIsImageUploading(false);
    }
  }, [toast]);

  // Returns false when the typed URL is invalid, so Save can stop at the field.
  const handleAddUrl = (): boolean => {
    const trimmed = urlInput.trim();
    if (!trimmed) return true;
    const parsed = documentLinkUrlSchema.safeParse(trimmed);
    if (!parsed.success) {
      setUrlError(parsed.error.issues[0].message);
      return false;
    }
    appendDocLink({ url: trimmed });
    setUrlInput('');
    setUrlError('');
    return true;
  };

  const handleCopyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: 'URL copied', status: 'success', duration: 2000 });
    } catch {
      toast({ title: 'The URL could not be copied', status: 'error', duration: 3000 });
    }
  };

  const handleSaveClick = () => {
    if (!handleAddUrl()) {
      urlInputRef.current?.focus();
      return;
    }
    handleSave();
  };

  const handleFileUpload = async (file: File) => {
    try {
      setIsUploading(true);
      const { url } = await uploadFileToS3(file, { folder: 'task-documents' });
      appendDocLink({ url });
    } catch {
      toast({ title: 'Upload failed', status: 'error', duration: 3000 });
    } finally {
      setIsUploading(false);
    }
  };

  const taskLabel = row ? `${rowIdx + 1}. ${row.name || 'Untitled task'}` : '';

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size={{ base: 'full', md: 'md' }}>
      <DrawerOverlay backdropFilter="blur(2px)" />
      <DrawerContent bg="bg-surface" data-testid="wfv4-config-panel">
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" borderColor="border-default">
          <Text fontSize="md" fontWeight="semibold" color="text-primary">Configure Task</Text>
          {taskLabel && (
            <Text fontSize="sm" color="text-secondary" fontWeight="normal" mt={1}>{taskLabel}</Text>
          )}
        </DrawerHeader>

        <DrawerBody overflowY="auto" py={4}>
          <VStack spacing={6} align="stretch">
            {/* Intro — image upload */}
            {watchedType === TaskType.INTRO && (
              <Box>
                <Text fontWeight="medium" color="text-primary" mb={3}>Intro Image</Text>
                <VStack spacing={3} align="stretch">
                  <FormControl>
                    <FormLabel fontSize="sm" color="text-secondary">Image URL</FormLabel>
                    <HStack>
                      <Input
                        size="sm"
                        placeholder="https://example.com/image.jpg"
                        value={imageUrl}
                        onChange={(e) => setImageUrl(e.target.value)}
                        bg="bg-surface"
                        borderColor="border-default"
                        data-testid="wfv4-config-intro-image-url-input"
                      />
                      <IconButton
                        aria-label="Upload image"
                        icon={<MdUploadFile />}
                        size="sm"
                        variant="outline"
                        colorScheme="brand"
                        isLoading={isImageUploading}
                        data-testid="wfv4-config-intro-upload-btn"
                        onClick={() => imageFileRef.current?.click()}
                        flexShrink={0}
                      />
                    </HStack>
                    <input
                      ref={imageFileRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleImageUpload(file);
                        e.target.value = '';
                      }}
                    />
                  </FormControl>
                  {isImageUploading && <Progress size="sm" isIndeterminate colorScheme="brand" borderRadius="full" />}
                  {imageUrl && (
                    <Image
                      src={imageUrl}
                      alt="Intro image preview"
                      maxH="150px"
                      objectFit="contain"
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor="border-default"
                    />
                  )}
                </VStack>
                <Divider mt={6} borderColor="border-default" />
              </Box>
            )}

            {/* Location Check — nearby master data (optional alternative to a fixed point) */}
            {watchedType === TaskType.LOCATION_CHECK && (
              <Box>
                <Text fontWeight="medium" color="text-primary" mb={3}>Nearby Master Data (optional)</Text>
                <Text fontSize="sm" color="text-secondary" mb={3}>
                  When set, the shared location is checked against nearby rows in this dataset instead of a
                  fixed point below. One nearby match is verified immediately; multiple matches let the user
                  pick which one before verifying.
                </Text>
                <VStack spacing={3} align="stretch">
                  <FormControl>
                    <FormLabel fontSize="sm" color="text-secondary">Dataset</FormLabel>
                    <Select
                      size="sm"
                      placeholder="None (use fixed coordinates below)"
                      bg="bg-surface"
                      borderColor="border-default"
                      value={form.watch('dataSourceConfig.dataset') ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        form.setValue('dataSourceConfig.dataset', val || undefined);
                        form.setValue('dataSourceConfig.source', val ? 'master_data' : (undefined as any));
                      }}
                      data-testid="wfv4-config-location-dataset-select"
                    >
                      {form.watch('dataSourceConfig.dataset')
                        && !masterDataDatasets.includes(form.watch('dataSourceConfig.dataset') as string) && (
                        <option value={form.watch('dataSourceConfig.dataset')}>
                          {form.watch('dataSourceConfig.dataset')}
                        </option>
                      )}
                      {masterDataDatasets.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </Select>
                  </FormControl>
                  {watchedMasterDataDataset && (() => {
                    const currentRadiusField = form.watch('dataSourceConfig.lookupField');
                    const numberFieldDefs = masterDataFieldDefs.filter((def) => def.data_type === 'number');
                    const currentInList = !currentRadiusField || numberFieldDefs.some((def) => def.field_name === currentRadiusField);
                    return (
                      <FormControl>
                        <FormLabel fontSize="sm" color="text-secondary">Radius Field</FormLabel>
                        {(numberFieldDefs.length > 0 || !currentInList) ? (
                          <RadioGroup
                            value={currentRadiusField ?? ''}
                            onChange={(val) => form.setValue('dataSourceConfig.lookupField', val || undefined)}
                          >
                            <Stack spacing={1}>
                              {!currentInList && (
                                <Radio value={currentRadiusField} size="sm">
                                  {currentRadiusField} (not a defined number field)
                                </Radio>
                              )}
                              {numberFieldDefs.map((def) => (
                                <Radio
                                  key={def.field_name}
                                  value={def.field_name}
                                  size="sm"
                                  data-testid={`wfv4-config-location-radius-field-${def.field_name}`}
                                >
                                  {def.label} ({def.field_name})
                                </Radio>
                              ))}
                            </Stack>
                          </RadioGroup>
                        ) : (
                          <Text fontSize="xs" color="text-muted">
                            No number fields defined on this dataset yet — add one under Master Data
                            field definitions, then it will appear here.
                          </Text>
                        )}
                        <Text fontSize="xs" color="text-muted" mt={1}>
                          Field on each dataset row holding its own check-in radius, in metres. Defaults to
                          &quot;radius&quot; when none is selected.
                        </Text>
                      </FormControl>
                    );
                  })()}
                </VStack>
                <Divider mt={6} borderColor="border-default" />
              </Box>
            )}

            {/* Location / Location Check — fixed-point coordinate config */}
            {(watchedType === TaskType.LOCATION
              || (watchedType === TaskType.LOCATION_CHECK && !watchedMasterDataDataset)) && (
              <Box>
                <Text fontWeight="medium" color="text-primary" mb={3}>
                  {watchedType === TaskType.LOCATION_CHECK ? 'Location Check' : 'Location'} Coordinates
                </Text>
                <VStack spacing={3} align="stretch">
                  <FormControl>
                    <FormLabel fontSize="sm" color="text-secondary">Paste Google Maps link</FormLabel>
                    <HStack>
                      <Input
                        size="sm"
                        placeholder="https://maps.google.com/..."
                        value={mapsLinkInput}
                        onChange={(e) => setMapsLinkInput(e.target.value)}
                        bg="bg-surface"
                        borderColor="border-default"
                        data-testid="wfv4-config-maps-link-input"
                      />
                      <Button
                        size="sm"
                        colorScheme="brand"
                        variant="outline"
                        flexShrink={0}
                        data-testid="wfv4-config-maps-parse-btn"
                        onClick={() => {
                          const parsed = parseGoogleMapsUrl(mapsLinkInput);
                          if (parsed) {
                            setLocationState((prev) => ({
                              ...prev,
                              lat: String(parsed.lat),
                              lng: String(parsed.lng),
                            }));
                            setMapsLinkInput('');
                          } else {
                            toast({ title: 'Could not extract coordinates from that URL', status: 'warning', duration: 3000 });
                          }
                        }}
                      >
                        Parse
                      </Button>
                    </HStack>
                    <Link
                      href="https://www.google.com/maps"
                      target="_blank"
                      color="brand.500"
                      fontSize="xs"
                      mt={1}
                      display="inline-block"
                    >
                      Open Google Maps
                    </Link>
                  </FormControl>

                  <Stack direction={{ base: 'column', sm: 'row' }} spacing={3} align={{ base: 'stretch', sm: 'flex-start' }}>
                    <FormControl flex={1}>
                      <FormLabel fontSize="sm" color="text-secondary">Latitude</FormLabel>
                      <Input
                        size="sm"
                        type="number"
                        step="any"
                        placeholder="12.3456"
                        value={locationState.lat}
                        onChange={(e) => setLocationState((prev) => ({ ...prev, lat: e.target.value }))}
                        bg="bg-surface"
                        borderColor="border-default"
                        data-testid="wfv4-config-location-lat"
                      />
                    </FormControl>
                    <FormControl flex={1}>
                      <FormLabel fontSize="sm" color="text-secondary">Longitude</FormLabel>
                      <Input
                        size="sm"
                        type="number"
                        step="any"
                        placeholder="78.9012"
                        value={locationState.lng}
                        onChange={(e) => setLocationState((prev) => ({ ...prev, lng: e.target.value }))}
                        bg="bg-surface"
                        borderColor="border-default"
                        data-testid="wfv4-config-location-lng"
                      />
                    </FormControl>
                    <FormControl
                      w={{ base: 'full', sm: '110px' }}
                      flexShrink={0}
                      isInvalid={watchedType === TaskType.LOCATION_CHECK && !(Number(locationState.radius) > 0)}
                    >
                      <HStack mb={1} spacing={1}>
                        <FormLabel fontSize="sm" color="text-secondary" mb={0}>
                          Radius (m) {watchedType === TaskType.LOCATION_CHECK && <Text as="span" color="red.400">*</Text>}
                        </FormLabel>
                        <Tooltip
                          label={watchedType === TaskType.LOCATION_CHECK
                            ? 'Radius in metres. Required — users must be within this distance to pass verification.'
                            : 'Radius in metres. Set to 0 to skip radius check.'}
                          hasArrow
                          placement="top"
                        >
                          <Box as="span" color="text-muted" cursor="help"><MdHelpOutline size={14} /></Box>
                        </Tooltip>
                      </HStack>
                      <Input
                        size="sm"
                        type="number"
                        min={watchedType === TaskType.LOCATION_CHECK ? 1 : 0}
                        step={1}
                        placeholder={watchedType === TaskType.LOCATION_CHECK ? '25' : '0'}
                        value={locationState.radius}
                        onChange={(e) => setLocationState((prev) => ({ ...prev, radius: e.target.value }))}
                        bg="bg-surface"
                        borderColor="border-default"
                        data-testid="wfv4-config-location-radius"
                      />
                      {watchedType === TaskType.LOCATION_CHECK && !(Number(locationState.radius) > 0) && (
                        <Text fontSize="xs" color="red.400" mt={1}>A radius greater than 0 is required for Location Check.</Text>
                      )}
                    </FormControl>
                  </Stack>
                </VStack>
                <Divider mt={6} borderColor="border-default" />
              </Box>
            )}

            {/* Address — free-text config (no coordinate verification) */}
            {watchedType === TaskType.ADDRESS && (
              <Box>
                <Text fontWeight="medium" color="text-primary" mb={3}>Address Details</Text>
                <Text fontSize="sm" color="text-secondary" mb={3}>
                  Optional notes shown to whoever reviews the submitted address (e.g. expected format). The user's
                  shared address itself is captured automatically and doesn't need to be configured here.
                </Text>
                <FormControl>
                  <FormLabel fontSize="sm" color="text-secondary">Additional Info</FormLabel>
                  <Input
                    size="sm"
                    placeholder="e.g. Must include landmark"
                    bg="bg-surface"
                    borderColor="border-default"
                    data-testid="wfv4-config-address-info-input"
                    {...form.register('data')}
                  />
                </FormControl>
                <Divider mt={6} borderColor="border-default" />
              </Box>
            )}

            {/* Photo/Document/Media — extraction verification config */}
            {[TaskType.PHOTO, TaskType.DOCUMENT, TaskType.MEDIA].includes(watchedType as TaskType) && (
              <ExtractionConfigSection
                control={form.control}
                setValue={form.setValue}
              />
            )}

            {/* Dynamic Choice — data source config */}
            {watchedType === TaskType.DYNAMIC_CHOICE && (
              <Box>
                <Text fontWeight="medium" color="text-primary" mb={3}>Data Source Configuration</Text>
                <DataSourceConfigSection
                  control={form.control}
                  setValue={form.setValue}
                  existingTasks={existingTasks}
                  currentTaskIndex={rowIdx + 1}
                  flowsEnabled={flowsEnabled}
                  masterDataFieldDefs={masterDataFieldDefs}
                  masterDataDatasets={masterDataDatasets}
                />
                <Divider mt={6} borderColor="border-default" />
              </Box>
            )}

            {/* Role — which dataset marks a sender as "assisted" */}
            {watchedType === TaskType.ROLE && (
              <Box>
                <Text fontWeight="medium" color="text-primary" mb={3}>Role Dataset</Text>
                <Text fontSize="sm" color="text-secondary" mb={3}>
                  When set, a sender whose mobile number is a key in this dataset answers
                  &quot;assisted&quot; (e.g. a security guard checking someone in); everyone else answers
                  &quot;self&quot;. Leave unset to always answer &quot;self&quot;.
                </Text>
                <FormControl>
                  <FormLabel fontSize="sm" color="text-secondary">Dataset</FormLabel>
                  <Select
                    size="sm"
                    placeholder="None (always self)"
                    bg="bg-surface"
                    borderColor="border-default"
                    value={form.watch('dataSourceConfig.dataset') ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      form.setValue('dataSourceConfig.dataset', val || undefined);
                      form.setValue('dataSourceConfig.source', val ? 'master_data' : (undefined as any));
                    }}
                    data-testid="wfv4-config-role-dataset-select"
                  >
                    {form.watch('dataSourceConfig.dataset')
                      && !masterDataDatasets.includes(form.watch('dataSourceConfig.dataset') as string) && (
                      <option value={form.watch('dataSourceConfig.dataset')}>
                        {form.watch('dataSourceConfig.dataset')}
                      </option>
                    )}
                    {masterDataDatasets.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </Select>
                </FormControl>
                <Divider mt={6} borderColor="border-default" />
              </Box>
            )}

            {/* Counter — daily cap dataset and optional dedupe identity */}
            {watchedType === TaskType.COUNTER && (
              <Box>
                <Text fontWeight="medium" color="text-primary" mb={3}>Counter Configuration</Text>
                <Text fontSize="sm" color="text-secondary" mb={3}>
                  The daily cap is read live from this dataset — a row matched on Match Field against this
                  bot&apos;s own short code (or Code Source Task&apos;s answer, if set), with the limit in
                  that row&apos;s Count Field.
                </Text>
                <VStack spacing={3} align="stretch">
                  <FormControl isRequired>
                    <FormLabel fontSize="sm" color="text-secondary">Dataset</FormLabel>
                    <Select
                      size="sm"
                      placeholder="Select dataset"
                      bg="bg-surface"
                      borderColor="border-default"
                      value={form.watch('dataSourceConfig.dataset') ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        form.setValue('dataSourceConfig.dataset', val || undefined);
                        form.setValue('dataSourceConfig.source', val ? 'master_data' : (undefined as any));
                      }}
                      data-testid="wfv4-config-counter-dataset-select"
                    >
                      {form.watch('dataSourceConfig.dataset')
                        && !masterDataDatasets.includes(form.watch('dataSourceConfig.dataset') as string) && (
                        <option value={form.watch('dataSourceConfig.dataset')}>
                          {form.watch('dataSourceConfig.dataset')}
                        </option>
                      )}
                      {masterDataDatasets.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </Select>
                  </FormControl>
                  {watchedMasterDataDataset && (() => {
                    const currentMatchField = form.watch('dataSourceConfig.lookupField');
                    const currentInList = !currentMatchField
                      || currentMatchField === 'key'
                      || currentMatchField === 'label'
                      || masterDataFieldDefs.some((def) => def.field_name === currentMatchField);
                    return (
                      <FormControl isRequired>
                        <FormLabel fontSize="sm" color="text-secondary">Match Field</FormLabel>
                        <RadioGroup
                          value={currentMatchField ?? ''}
                          onChange={(val) => form.setValue('dataSourceConfig.lookupField', val || undefined)}
                        >
                          <Stack spacing={1}>
                            {!currentInList && (
                              <Radio value={currentMatchField} size="sm">
                                {currentMatchField} (not a defined field)
                              </Radio>
                            )}
                            <Radio value="key" size="sm" data-testid="wfv4-config-counter-lookup-field-key">
                              Key
                            </Radio>
                            <Radio value="label" size="sm" data-testid="wfv4-config-counter-lookup-field-label">
                              Label
                            </Radio>
                            {masterDataFieldDefs.map((def) => (
                              <Radio
                                key={def.field_name}
                                value={def.field_name}
                                size="sm"
                                data-testid={`wfv4-config-counter-lookup-field-${def.field_name}`}
                              >
                                {def.label} ({def.field_name})
                              </Radio>
                            ))}
                          </Stack>
                        </RadioGroup>
                        <Text fontSize="xs" color="text-muted" mt={1}>
                          Field on each dataset row holding this bot&apos;s own short code (or Code Source
                          Task&apos;s answer) — Key or Label themselves, or an Additional Info field.
                        </Text>
                      </FormControl>
                    );
                  })()}
                  {watchedMasterDataDataset && (() => {
                    const currentCountField = form.watch('counterConfig.countField');
                    const numberFieldDefs = masterDataFieldDefs.filter((def) => def.data_type === 'number');
                    const currentInList = !currentCountField || numberFieldDefs.some((def) => def.field_name === currentCountField);
                    return (
                      <FormControl>
                        <FormLabel fontSize="sm" color="text-secondary">Count Field</FormLabel>
                        {(numberFieldDefs.length > 0 || !currentInList) ? (
                          <RadioGroup
                            value={currentCountField ?? ''}
                            onChange={(val) => form.setValue('counterConfig.countField', val || undefined)}
                          >
                            <Stack spacing={1}>
                              {!currentInList && (
                                <Radio value={currentCountField} size="sm">
                                  {currentCountField} (not a defined number field)
                                </Radio>
                              )}
                              {numberFieldDefs.map((def) => (
                                <Radio
                                  key={def.field_name}
                                  value={def.field_name}
                                  size="sm"
                                  data-testid={`wfv4-config-counter-count-field-${def.field_name}`}
                                >
                                  {def.label} ({def.field_name})
                                </Radio>
                              ))}
                            </Stack>
                          </RadioGroup>
                        ) : (
                          <Text fontSize="xs" color="text-muted">
                            No number fields defined on this dataset yet — add one under Master Data
                            field definitions, then it will appear here.
                          </Text>
                        )}
                        <Text fontSize="xs" color="text-muted" mt={1}>
                          Field on the matched row holding the cap itself. Defaults to
                          &quot;max_count&quot; when none is selected.
                        </Text>
                      </FormControl>
                    );
                  })()}
                  <FormControl>
                    <FormLabel fontSize="sm" color="text-secondary">Code Source Task (optional)</FormLabel>
                    <Select
                      size="sm"
                      placeholder="None (match against this bot's own short code)"
                      bg="bg-surface"
                      borderColor="border-default"
                      value={form.watch('counterConfig.codeTaskId') ?? ''}
                      onChange={(e) => form.setValue('counterConfig.codeTaskId', e.target.value || undefined)}
                      data-testid="wfv4-config-counter-code-task-select"
                    >
                      {existingTasks.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </Select>
                    <Text fontSize="xs" color="text-muted" mt={1}>
                      When set, this task&apos;s submitted value is matched against Match Field instead of
                      this bot&apos;s own short code — for a single shared bot where the contractor/site code
                      arrives as a task answer (e.g. a <code>/start</code> attribute). Falls back to the short
                      code if that task has no answer (e.g. it auto-skipped).
                    </Text>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm" color="text-secondary">Dedupe Identity Task (optional)</FormLabel>
                    <Select
                      size="sm"
                      placeholder="None (count every increment, no dedupe)"
                      bg="bg-surface"
                      borderColor="border-default"
                      value={form.watch('counterConfig.identityTaskId') ?? ''}
                      onChange={(e) => form.setValue('counterConfig.identityTaskId', e.target.value || undefined)}
                      data-testid="wfv4-config-counter-identity-task-select"
                    >
                      {existingTasks.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </Select>
                    <Text fontSize="xs" color="text-muted" mt={1}>
                      When set, this task&apos;s submitted value identifies who the increment counts for —
                      an identity that already counted today is rejected instead of counted again. Falls back
                      to the sender&apos;s own mobile if that task has no answer (e.g. it auto-skipped).
                    </Text>
                  </FormControl>
                  {form.watch('counterConfig.identityTaskId') && (
                    <FormControl>
                      <FormLabel fontSize="sm" color="text-secondary">Shared Identities (optional)</FormLabel>
                      <Input
                        size="sm"
                        placeholder="e.g. 9999999999"
                        bg="bg-surface"
                        borderColor="border-default"
                        {...form.register('counterConfig.sharedIdentities')}
                        data-testid="wfv4-config-counter-shared-identities-input"
                      />
                      <Text fontSize="xs" color="text-muted" mt={1}>
                        Comma-separated values many people share, such as a placeholder mobile typed for
                        workers without a phone. An entry with one of these is always counted, never
                        rejected as already checked in.
                      </Text>
                    </FormControl>
                  )}
                </VStack>
                <Divider mt={6} borderColor="border-default" />
              </Box>
            )}

            {/* Number — validation config */}
            {watchedType === TaskType.NUMBER && (
              <Box>
                <Text fontWeight="medium" color="text-primary" mb={3}>Number Validation</Text>
                <ValidationConfigSection
                  control={form.control}
                  setValue={form.setValue}
                  existingTasks={existingTasks}
                  currentTaskIndex={rowIdx + 1}
                />
                <Divider mt={6} borderColor="border-default" />
              </Box>
            )}

            {/* Text — master data lookup */}
            {watchedType === TaskType.TEXT && (
              <Box>
                <Text fontWeight="medium" color="text-primary" mb={3}>Master Data Lookup</Text>
                <Text fontSize="sm" color="text-secondary" mb={3}>
                  When set, entering a matching value loads its attributes for use in linked workflows.
                </Text>
                <VStack spacing={3} align="stretch">
                  <FormControl>
                    <FormLabel fontSize="sm" color="text-secondary">Dataset (optional)</FormLabel>
                    <Select
                      size="sm"
                      placeholder="Select dataset"
                      bg="bg-surface"
                      borderColor="border-default"
                      value={form.watch('dataSourceConfig.dataset') ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        form.setValue('dataSourceConfig.dataset', val || undefined);
                        form.setValue('dataSourceConfig.source', val ? 'master_data' : (undefined as any));
                      }}
                    >
                      {form.watch('dataSourceConfig.dataset')
                        && !masterDataDatasets.includes(form.watch('dataSourceConfig.dataset') as string) && (
                        <option value={form.watch('dataSourceConfig.dataset')}>
                          {form.watch('dataSourceConfig.dataset')}
                        </option>
                      )}
                      {masterDataDatasets.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </Select>
                    <Text fontSize="xs" color="text-muted" mt={1} data-testid="wfv4-config-text-dataset-hint">
                      Lists datasets and master data categories, including ones with no rows yet. A
                      lookup against an empty dataset matches nothing, so add its rows before going live.
                    </Text>
                  </FormControl>
                  {form.watch('dataSourceConfig.dataset') && (() => {
                    const configuredFields = form.watch('dataSourceConfig.lookupFields')
                      ?? (form.watch('dataSourceConfig.lookupField')
                        ? [form.watch('dataSourceConfig.lookupField') as string]
                        : []);
                    const toggleField = (fieldName: string, checked: boolean) => {
                      const next = checked
                        ? [...configuredFields, fieldName]
                        : configuredFields.filter((f) => f !== fieldName);
                      form.setValue('dataSourceConfig.lookupFields', next.length > 0 ? next : undefined);
                      form.setValue('dataSourceConfig.lookupField', undefined);
                    };
                    return (
                      <FormControl>
                        <FormLabel fontSize="sm" color="text-secondary">Lookup Fields</FormLabel>
                        <Stack spacing={1}>
                          <Checkbox
                            size="sm"
                            isChecked={configuredFields.includes('key')}
                            onChange={(e) => toggleField('key', e.target.checked)}
                          >
                            Key
                          </Checkbox>
                          <Checkbox
                            size="sm"
                            isChecked={configuredFields.includes('label')}
                            onChange={(e) => toggleField('label', e.target.checked)}
                          >
                            Label
                          </Checkbox>
                          {masterDataFieldDefs.map((def) => (
                            <Checkbox
                              key={def.field_name}
                              size="sm"
                              isChecked={configuredFields.includes(def.field_name)}
                              onChange={(e) => toggleField(def.field_name, e.target.checked)}
                            >
                              {def.label} ({def.field_name})
                            </Checkbox>
                          ))}
                        </Stack>
                        <Text fontSize="xs" color="text-muted" mt={1}>
                          Select which fields the entered value must match. Leave all unchecked to default to matching by Key (exact mode) or Key + Label (partial search mode).
                        </Text>
                      </FormControl>
                    );
                  })()}
                  {form.watch('dataSourceConfig.dataset') && (
                    <FormControl>
                      <FormLabel fontSize="sm" color="text-secondary">Match Mode</FormLabel>
                      <Select
                        size="sm"
                        bg="bg-surface"
                        borderColor="border-default"
                        value={form.watch('dataSourceConfig.matchMode') ?? 'exact'}
                        onChange={(e) =>
                          form.setValue(
                            'dataSourceConfig.matchMode',
                            (e.target.value as 'exact' | 'search') || undefined
                          )
                        }
                      >
                        <option value="exact">Exact match</option>
                        <option value="search">Partial search (show matching list)</option>
                      </Select>
                      <Text fontSize="xs" color="text-muted" mt={1}>
                        Exact requires the typed value to match precisely. Partial search finds records containing the typed text and lets the user pick from a list.
                      </Text>
                    </FormControl>
                  )}
                  {form.watch('dataSourceConfig.dataset') && form.watch('dataSourceConfig.matchMode') === 'search' && (
                    <>
                      <FormControl>
                        <FormLabel fontSize="sm" color="text-secondary">Result List Title Field</FormLabel>
                        <Select
                          size="sm"
                          bg="bg-surface"
                          borderColor="border-default"
                          value={form.watch('dataSourceConfig.titleField') ?? 'label'}
                          onChange={(e) =>
                            form.setValue('dataSourceConfig.titleField', e.target.value || undefined)
                          }
                        >
                          <option value="key">Key</option>
                          <option value="label">Label</option>
                          {masterDataFieldDefs.map((def) => (
                            <option key={def.field_name} value={def.field_name}>
                              {def.label} ({def.field_name})
                            </option>
                          ))}
                        </Select>
                        <Text fontSize="xs" color="text-muted" mt={1}>
                          Field shown as each result&apos;s title in the WhatsApp list. Defaults to Label.
                        </Text>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm" color="text-secondary">Result List Description Field</FormLabel>
                        <Select
                          size="sm"
                          bg="bg-surface"
                          borderColor="border-default"
                          value={form.watch('dataSourceConfig.descriptionField') ?? 'key'}
                          onChange={(e) =>
                            form.setValue('dataSourceConfig.descriptionField', e.target.value || undefined)
                          }
                        >
                          <option value="key">Key</option>
                          <option value="label">Label</option>
                          {masterDataFieldDefs.map((def) => (
                            <option key={def.field_name} value={def.field_name}>
                              {def.label} ({def.field_name})
                            </option>
                          ))}
                        </Select>
                        <Text fontSize="xs" color="text-muted" mt={1}>
                          Field shown as each result&apos;s description in the WhatsApp list. Defaults to Key.
                        </Text>
                      </FormControl>
                    </>
                  )}
                </VStack>
                <Divider mt={6} borderColor="border-default" />
              </Box>
            )}

            {/* Auto-fill — dynamic_choice always supports it; text_required and
                location_check only once a master_data lookup is configured (matching
                shouldIncludeDataSource in api.ts); photo/doc/media once verification is
                switched on, mirroring the extracted fields back onto this task's own row
                (see extractionGate.mirrorFields, internal/workflow/types/extraction.go) */}
            {showAutoFillSection && (
              <Box>
                <AutoFillConfigSection
                  entries={autoFillEntries}
                  onChange={setAutoFillEntries}
                  laterTasks={laterTasks}
                  valueRefTasks={valueRefTasks}
                  triggerDescription={
                    isExtractionSource
                      ? 'When this upload passes verification'
                      : 'When a match is selected here'
                  }
                />
                <Divider mt={6} borderColor="border-default" />
              </Box>
            )}

            {/* Actions */}
            <Box>
              <FormLabel color="text-primary" mb={3}>Task Actions</FormLabel>
              <Controller
                name="actions"
                control={form.control}
                render={({ field }) => (
                  <CheckboxGroup value={field.value ?? []} onChange={field.onChange}>
                    <SimpleGrid columns={2} spacing={3}>
                      {actions
                        // TEAM_ALERT and WRITEBACK are only offered on task types wired to fire them.
                        .filter((actionKey) => actionKey !== 'TEAM_ALERT' || teamAlertAllowed)
                        .filter((actionKey) => actionKey !== 'WRITEBACK' || writebackAllowed)
                        .map((actionKey) => (
                        <Checkbox key={actionKey} value={ActionMap[actionKey]} colorScheme="brand" data-testid={`wfv4-config-action-${actionKey.toLowerCase()}`}>
                          {actionKey}
                        </Checkbox>
                      ))}
                    </SimpleGrid>
                  </CheckboxGroup>
                )}
              />
            </Box>

            {/* STARTNEW config */}
            {hasStartnew && (
              <>
                <Divider borderColor="border-default" />
                <StartnewConfigSection
                  control={form.control}
                  existingTasks={existingTasks}
                  attributeMappingEntries={attributeMappingEntries}
                  valueRefTasks={valueRefTasks}
                  onAttributeMappingChange={setAttributeMappingEntries}
                  availableWorkflows={availableWorkflows}
                />
              </>
            )}

            {/* SKIPPABLE config */}
            {hasSkippable && (
              <>
                <Divider borderColor="border-default" />
                <SkippableConfigSection
                  control={form.control}
                  setValue={form.setValue}
                  existingTasks={existingTasks}
                  currentTaskIndex={rowIdx + 1}
                />
              </>
            )}

            {/* MAKERCHECKER config */}
            {showApprovalConfig && (
              <>
                <Divider borderColor="border-default" />
                <MakerCheckerConfigSection
                  control={form.control}
                  setValue={form.setValue}
                  taskType={watchedType as TaskType}
                  availableTasks={approvalSourceTasks ?? existingTasks}
                />
              </>
            )}

            {/* TEAM_ALERT config (trimmed: contacts + template) */}
            {showTeamAlertConfig && (
              <>
                <Divider borderColor="border-default" />
                <MakerCheckerConfigSection
                  control={form.control}
                  setValue={form.setValue}
                  taskType={watchedType as TaskType}
                  availableTasks={existingTasks}
                  mode="alert"
                />
              </>
            )}

            {/* WRITEBACK config (G11) — end task only, mirrors TEAM_ALERT's completion-time firing */}
            {showWritebackConfig && (
              <>
                <Divider borderColor="border-default" />
                <WritebackConfigSection
                  policies={writebackPolicies}
                  onAddPolicy={handleAddWritebackPolicy}
                  onRemovePolicy={handleRemoveWritebackPolicy}
                  onPolicyChange={handleWritebackPolicyChange}
                  valueRefTasks={valueRefTasks}
                  masterDataDatasets={masterDataDatasets}
                  fieldDefsByDataset={writebackFieldDefsByDataset}
                  errors={writebackPolicies.map((_, index) => ({
                    dataset: form.formState.errors.writebackConfigs?.[index]?.dataset?.message,
                    keyRef: form.formState.errors.writebackConfigs?.[index]?.keyRef?.message,
                    labelRef: form.formState.errors.writebackConfigs?.[index]?.labelRef?.message,
                    fields: form.formState.errors.writebackConfigs?.[index]?.fields?.message,
                  }))}
                  noPoliciesError={form.formState.errors.writebackConfigs?.message}
                />
              </>
            )}

            <Divider borderColor="border-default" />

            {/* Soft Due Date (long_running lifecycle only) */}
            {chatbotLifecycle === 'long_running' && (
              <>
                <Box>
                  <Text fontWeight="medium" color="text-primary" mb={3}>Soft Due Date</Text>
                  <Stack direction={{ base: 'column', sm: 'row' }} spacing={4}>
                    <FormField
                      label="Value"
                      name="softTtlValue"
                      type="number"
                      register={form.register}
                      min={1}
                    />
                    <FormField
                      label="Unit"
                      name="softTtlUnit"
                      type="select"
                      register={form.register}
                      options={[{ value: 'hours', label: 'Hours' }, { value: 'days', label: 'Days' }]}
                    />
                  </Stack>
                </Box>
                <Divider borderColor="border-default" />
              </>
            )}

            {/* Reference Documents */}
            <Box>
              <Text fontWeight="medium" color="text-primary" mb={flowsEnabled ? 1 : 3}>Reference Documents</Text>
              {flowsEnabled && (
                <Text fontSize="xs" color="text-secondary" mb={3}>
                  This workflow runs as a WhatsApp Flow: up to 3 JPEG or PNG images, 1MB each.
                </Text>
              )}
              <FormControl isInvalid={!!urlError} mb={2}>
                <HStack>
                  <Input
                    ref={urlInputRef}
                    size="sm"
                    value={urlInput}
                    onChange={(e) => { setUrlInput(e.target.value); setUrlError(''); }}
                    placeholder="Paste a URL"
                    bg="bg-surface"
                    borderColor="border-default"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddUrl(); } }}
                    data-testid="wfv4-config-doc-url-input"
                  />
                  <Button size="sm" variant="outline" flexShrink={0} leftIcon={<MdAdd />} onClick={handleAddUrl} data-testid="wfv4-config-doc-add-btn">
                    Add
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="brand"
                    flexShrink={0}
                    leftIcon={<MdUploadFile />}
                    isLoading={isUploading}
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="wfv4-config-doc-upload-btn"
                  >
                    Upload
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={(e) => { const file = e.target.files?.[0]; if (file) handleFileUpload(file); e.target.value = ''; }}
                  />
                </HStack>
                <FormErrorMessage data-testid="wfv4-config-doc-url-error">{urlError}</FormErrorMessage>
              </FormControl>
              {docLinkFields.map((field, index) => (
                <HStack key={field.id} mb={1} align="flex-start">
                  <Text fontSize="sm" color="text-secondary" flex={1} wordBreak="break-all" data-testid={`wfv4-config-doc-link-${index}`}>{field.url}</Text>
                  <IconButton aria-label="Copy URL" icon={<MdContentCopy />} size="xs" variant="ghost" onClick={() => handleCopyUrl(field.url)} data-testid={`wfv4-config-doc-copy-${index}`} />
                  <IconButton aria-label="Remove" icon={<MdDelete />} size="xs" variant="ghost" colorScheme="red" onClick={() => removeDocLink(index)} />
                </HStack>
              ))}
            </Box>

            <Divider borderColor="border-default" />

            {/* Task Tags */}
            <Box>
              <Text fontWeight="medium" color="text-primary" mb={3}>Task Tags</Text>
              {tags.map((tag, index) => (
                <HStack key={index} spacing={2} mb={2}>
                  <Input
                    size="sm"
                    placeholder="Key"
                    value={tag.key}
                    onChange={(e) => setTags((prev) => prev.map((t, i) => i === index ? { ...t, key: e.target.value } : t))}
                    bg="bg-surface"
                    borderColor="border-default"
                  />
                  <Input
                    size="sm"
                    placeholder="Value"
                    value={String(tag.value)}
                    onChange={(e) => setTags((prev) => prev.map((t, i) => i === index ? { ...t, value: e.target.value } : t))}
                    bg="bg-surface"
                    borderColor="border-default"
                  />
                  <IconButton
                    aria-label="Remove tag"
                    icon={<MdDelete />}
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => setTags((prev) => prev.filter((_, i) => i !== index))}
                  />
                </HStack>
              ))}
              <Button
                leftIcon={<MdAdd />}
                size="xs"
                variant="ghost"
                onClick={() => setTags((prev) => [...prev, { key: '', value: '' }])}
              >
                Add Tag
              </Button>
            </Box>

            <Divider borderColor="border-default" />

            {/* Mutable + Mask Output */}
            <VStack spacing={3} align="flex-start">
              <Controller
                name="mutable"
                control={form.control}
                render={({ field }) => (
                  <Checkbox isChecked={field.value} onChange={field.onChange} colorScheme="brand" data-testid="wfv4-config-mutable-checkbox">
                    <Box>
                      <Text fontSize="sm" color="text-primary">Allow editing</Text>
                      <Text fontSize="xs" color="text-secondary">Users can edit this task's value after submission</Text>
                    </Box>
                  </Checkbox>
                )}
              />
              <Controller
                name="maskOutput"
                control={form.control}
                render={({ field }) => (
                  <Checkbox isChecked={field.value} onChange={field.onChange} colorScheme="brand" data-testid="wfv4-config-mask-output-checkbox">
                    <Box>
                      <Text fontSize="sm" color="text-primary">Mask output</Text>
                      <Text fontSize="xs" color="text-secondary">Value shown as **** in reports</Text>
                    </Box>
                  </Checkbox>
                )}
              />
              <Controller
                name="requirePrefill"
                control={form.control}
                render={({ field }) => (
                  <Checkbox isChecked={field.value} onChange={field.onChange} colorScheme="brand" data-testid="wfv4-config-require-prefill-checkbox">
                    <Box>
                      <Text fontSize="sm" color="text-primary">Require prefill from QR code</Text>
                      <Text fontSize="xs" color="text-secondary">FORM only: /start is refused unless the QR code carries this task's value</Text>
                    </Box>
                  </Checkbox>
                )}
              />
            </VStack>
          </VStack>
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px" borderColor="border-default">
          <Button variant="ghost" mr={3} onClick={onClose} data-testid="wfv4-config-panel-cancel-btn">Cancel</Button>
          <Button colorScheme="brand" onClick={handleSaveClick} data-testid="wfv4-config-panel-save-btn">Save</Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
